import { area } from '@turf/area';
import { booleanIntersects } from '@turf/boolean-intersects';
import { buffer } from '@turf/buffer';
import { convex } from '@turf/convex';
import { union } from '@turf/union';
import type { Feature, LineString, Point, Polygon, MultiPolygon, Position } from 'geojson';

import type { VectorFeature } from '../data/model';
import { ANNOTATION_LIMITS } from '../annotations/model';
import { validAreaTopology } from '../annotations/areaTopology';

export type AreaFamily = 'polygon' | 'line' | 'point';
export type AreaOutput = 'polygon' | 'focus-area';
export const MAX_AREA_BUFFER_METRES = 100_000;

export interface AreaSelection {
  readonly count: number;
  readonly family: AreaFamily | null;
  readonly components: readonly (Polygon | LineString | Point)[];
  readonly pointCount: number;
  readonly error: string | null;
}

const feature = <G extends Polygon | LineString | Point>(geometry: G): Feature<G> => ({
  type: 'Feature',
  properties: {},
  geometry,
});

function positions(geometry: Polygon | LineString | Point): Position[] {
  return geometry.type === 'Point'
    ? [geometry.coordinates]
    : geometry.type === 'LineString'
      ? geometry.coordinates
      : geometry.coordinates.flat();
}

function checkGeography(coordinates: readonly Position[], distance = 0): void {
  if (!coordinates.length) throw new Error('Selected geometry is empty.');
  let west = Infinity,
    east = -Infinity,
    south = Infinity,
    north = -Infinity;
  for (const coordinate of coordinates) {
    const [x, y] = coordinate;
    if (x === undefined || y === undefined || coordinate.some((value) => !Number.isFinite(value)))
      throw new Error('Selected geometry contains invalid coordinates.');
    if (Math.abs(x) > 180 || Math.abs(y) > 80)
      throw new Error(
        'Create area supports local/regional geometry below 80° latitude, away from the antimeridian.',
      );
    west = Math.min(west, x);
    east = Math.max(east, x);
    south = Math.min(south, y);
    north = Math.max(north, y);
  }
  // Bound the angular radius of Turf's local azimuthal equidistant projection.
  // Also leave enough longitude clearance for the requested buffer at this latitude.
  const cosine = Math.cos((Math.max(Math.abs(south), Math.abs(north)) * Math.PI) / 180);
  const angularDistance = distance / 6_371_008.8;
  const clearance = (Math.asin(Math.min(1, Math.sin(angularDistance) / cosine)) * 180) / Math.PI;
  if (
    east - west > 180 ||
    Math.hypot((east - west) * cosine, north - south) > 20 ||
    west - clearance <= -180 ||
    east + clearance >= 180
  )
    throw new Error(
      'This selection or buffer is too wide or crosses the antimeridian. Use a local/regional selection.',
    );
}

function connected(
  components: readonly (Polygon | LineString)[],
  owners = components.map((_, index) => index),
  nodeCount = components.length,
): boolean {
  if (nodeCount < 2) return true;
  const boxes = components
    .map((geometry, index) => {
      const coords = positions(geometry);
      let west = Infinity,
        east = -Infinity,
        south = Infinity,
        north = -Infinity;
      for (const [x, y] of coords) {
        west = Math.min(west, x!);
        east = Math.max(east, x!);
        south = Math.min(south, y!);
        north = Math.max(north, y!);
      }
      return { index, west, east, south, north };
    })
    .sort((a, b) => a.west - b.west);
  const parents = Array.from({ length: nodeCount }, (_, index) => index);
  const root = (index: number): number => {
    while (parents[index] !== index) {
      parents[index] = parents[parents[index]!]!;
      index = parents[index]!;
    }
    return index;
  };
  let groups = nodeCount;
  let active: typeof boxes = [];
  for (const box of boxes) {
    active = active.filter((other) => other.east >= box.west);
    for (const other of active) {
      if (other.north < box.south || other.south > box.north) continue;
      const a = root(owners[box.index]!),
        b = root(owners[other.index]!);
      if (a !== b && booleanIntersects(components[box.index]!, components[other.index]!)) {
        parents[a] = b;
        groups -= 1;
        if (groups === 1) return true;
      }
    }
    active.push(box);
  }
  return groups === 1;
}

/** Pure, worker-compatible preflight. No union, hull or buffer runs here. */
export function prepareAreaSelection(features: readonly VectorFeature[]): AreaSelection {
  let family: AreaFamily | null = null;
  const components: (Polygon | LineString | Point)[] = [];
  const owners: number[] = [];
  try {
    if (!features.length) throw new Error('Select at least one vector feature.');
    for (const [featureIndex, { geometry }] of features.entries()) {
      const componentStart = components.length;
      if (!geometry) throw new Error('Selected features contain empty geometry.');
      if (!geometry.coordinates.length)
        throw new Error('Selected features contain empty geometry.');
      const next = geometry.type.includes('Polygon')
        ? 'polygon'
        : geometry.type.includes('LineString')
          ? 'line'
          : 'point';
      if (family && family !== next)
        return {
          count: features.length,
          family: null,
          components: [],
          pointCount: 0,
          error: 'Selected features must all be the same type (points, lines, or polygons).',
        };
      family = next;
      switch (geometry.type) {
        case 'Point':
        case 'LineString':
        case 'Polygon':
          components.push(geometry);
          break;
        case 'MultiPoint':
          components.push(
            ...geometry.coordinates.map((coordinates): Point => ({ type: 'Point', coordinates })),
          );
          break;
        case 'MultiLineString':
          components.push(
            ...geometry.coordinates.map((coordinates): LineString => ({
              type: 'LineString',
              coordinates,
            })),
          );
          break;
        case 'MultiPolygon':
          components.push(
            ...geometry.coordinates.map((coordinates): Polygon => ({
              type: 'Polygon',
              coordinates,
            })),
          );
          break;
      }
      for (let index = componentStart; index < components.length; index += 1)
        owners.push(featureIndex);
    }
    if (!components.length) throw new Error('Selected geometry is empty.');
    checkGeography(components.flatMap(positions));
    for (const component of components) {
      if (
        component.type === 'LineString' &&
        (component.coordinates.length < 2 ||
          !component.coordinates.some(
            ([x, y]) => x !== component.coordinates[0]![0] || y !== component.coordinates[0]![1],
          ))
      )
        throw new Error('Selected line geometry is invalid.');
      if (component.type === 'Polygon') {
        for (const ring of component.coordinates) {
          if (ring.length < 4 || ring[0]![0] !== ring.at(-1)![0] || ring[0]![1] !== ring.at(-1)![1])
            throw new Error('Selected polygon geometry is invalid.');
        }
        if (
          !component.coordinates.length ||
          !(area(component) > 0) ||
          !validAreaTopology(component)
        )
          throw new Error('Selected polygon geometry is invalid.');
      }
    }
    if (
      family !== 'point' &&
      !connected(
        components as (Polygon | LineString)[],
        family === 'polygon' ? owners : undefined,
        family === 'polygon' ? features.length : undefined,
      )
    )
      throw new Error(
        family === 'polygon'
          ? 'Selected polygon features must connect or overlap.'
          : 'Selected lines must connect or intersect.',
      );
    return {
      count: features.length,
      family,
      components,
      pointCount: family === 'point' ? components.length : 0,
      error: null,
    };
  } catch (error) {
    return {
      count: features.length,
      family,
      components: [],
      pointCount: 0,
      error: error instanceof Error ? error.message : 'Unable to analyse selected geometry.',
    };
  }
}

export function areaBufferError(selection: AreaSelection, metres: number): string | null {
  if (!Number.isFinite(metres) || metres < 0)
    return 'Enter a finite, non-negative buffer distance.';
  if (selection.family !== 'polygon' && metres === 0)
    return 'Points and lines require a buffer greater than 0.';
  if (metres > MAX_AREA_BUFFER_METRES) return 'Buffer distance must be 100 km or less.';
  return null;
}

function pointBase(components: readonly Point[]): Point | LineString | Polygon {
  const unique = [
    ...new Map(
      components.map((point) => [point.coordinates.slice(0, 2).join(','), point.coordinates]),
    ).values(),
  ];
  if (unique.length === 1) return { type: 'Point', coordinates: unique[0]! };
  if (unique.length >= 3) {
    const hull = convex({
      type: 'FeatureCollection',
      features: unique.map((coordinates) => feature({ type: 'Point', coordinates })),
    });
    if (hull && area(hull) > 0) return hull.geometry;
  }
  // A degenerate hull is a segment between sorted extrema, not an arbitrary pair.
  unique.sort((a, b) => a[0]! - b[0]! || a[1]! - b[1]!);
  return { type: 'LineString', coordinates: [unique[0]!, unique.at(-1)!] };
}

export function generateSelectionArea(
  selection: AreaSelection,
  metres: number,
): Polygon | MultiPolygon {
  if (selection.error) throw new Error(selection.error);
  const distanceError = areaBufferError(selection, metres);
  if (distanceError) throw new Error(distanceError);
  checkGeography(selection.components.flatMap(positions), metres);
  let base;
  if (selection.family === 'polygon') {
    const polygons = selection.components as readonly Polygon[];
    base =
      polygons.length === 1
        ? polygons[0]!
        : union({ type: 'FeatureCollection', features: polygons.map(feature) })?.geometry;
    if (!base) throw new Error('Unable to dissolve the selected polygon features.');
  } else if (selection.family === 'line') {
    base = {
      type: 'MultiLineString' as const,
      coordinates: (selection.components as readonly LineString[]).map((line) => line.coordinates),
    };
  } else base = pointBase(selection.components as readonly Point[]);
  const output =
    metres === 0 ? base : buffer(base, metres, { units: 'meters', steps: 16 })?.geometry;
  if (!output || (output.type !== 'Polygon' && output.type !== 'MultiPolygon'))
    throw new Error('Unable to generate a usable area from this selection.');
  const polygons =
    output.type === 'Polygon'
      ? [output]
      : output.coordinates.map((coordinates): Polygon => ({ type: 'Polygon', coordinates }));
  checkGeography(polygons.flatMap(positions));
  if (
    !(area(output) > 0) ||
    polygons.flatMap(positions).length > ANNOTATION_LIMITS.verticesPerAnnotation ||
    polygons.some((polygon) => !(area(polygon) > 0) || !validAreaTopology(polygon))
  )
    throw new Error('Generated area is invalid or exceeds the annotation vertex limit.');
  return output;
}
